import { Controller } from "stimulus";
import consumer from "../channels/consumer";
import { ENGINES } from "../transcription_lab/engines";

// Admin transcription test visio (app/views/transcription_lab/show.html.erb). Self-contained on
// purpose: it shares nothing with live_captions_controller.js, so the RDV visio captions are
// never affected by what gets tried here.
//
// Each participant transcribes their own microphone with the selected engine and relays the
// lines to the room through TranscriptionLabChannel; switching engine switches it for everyone.
// Engine protocols live in transcription_lab/engines.js.

const BUFFER_SIZE = 2048; // ~128 ms per callback at 16 kHz
const SPEECH_RMS = 0.015; // local voice threshold (sentence detection + latency)
const PREROLL_MS = 300; // audio kept from before the voice threshold, so first syllables survive
const VOICE_HANGOVER_MS = 250; // a pause shorter than this does not end a burst of speech
const MAX_TURN_MS = 15000; // client-turn engines: force a commit on long monologues
// Client-turn engines: a "sentence" with less voice than this was a click or a breath. Sent anyway,
// Canary and OpenAI turn such near-silent audio into a plausible word ("Oui.", "Merci.").
const MIN_VOICED_MS = 250;
const FINISH_WAIT_MS = 4000; // after "Arrêter", how long to wait for the last text

export default class extends Controller {
  static targets = [
    "jitsi", "engine", "openaiModel", "openaiModelField", "language", "silenceMs", "startButton",
    "stopButton", "meter", "status", "transcript", "log", "inviteLink"
  ];

  static values = {
    sessionUrl: String, jaasRoom: String, jaasToken: String, channelToken: String, speakerName: String
  };

  connect() {
    this.tabId = Math.random().toString(36).slice(2, 10);
    this.lines = new Map();
    this.muted = false;
    this.inviteLinkTarget.value = window.location.href;
    this.engineFieldsChanged();
    this.subscribe();
    this.startJitsi();
  }

  disconnect() {
    window.clearTimeout(this.jitsiRetry);
    this.teardown();
    if (this.subscription) consumer.subscriptions.remove(this.subscription);
    this.jitsi?.dispose();
  }

  // ---- Room: Jitsi + relay channel ----

  startJitsi() {
    if (!window.JitsiMeetExternalAPI) {
      this.jitsiRetry = window.setTimeout(() => this.startJitsi(), 200);
      return;
    }
    this.jitsi = new window.JitsiMeetExternalAPI("8x8.vc", {
      roomName: this.jaasRoomValue,
      parentNode: this.jitsiTarget,
      width: "100%",
      height: 520,
      lang: "fr",
      jwt: this.jaasTokenValue || undefined,
      userInfo: { displayName: this.speakerNameValue },
      configOverwrite: { disableRecordAudioNotification: true, p2p: { enabled: false } }
    });
    this.jitsi.addListener("audioMuteStatusChanged", ({ muted }) => {
      this.muted = muted;
      this.log(muted ? "micro coupé dans Jitsi : sous-titres en pause" : "micro réactivé dans Jitsi");
    });
  }

  subscribe() {
    this.subscription = consumer.subscriptions.create(
      { channel: "TranscriptionLabChannel", token: this.channelTokenValue },
      {
        connected: () => this.log("salle connectée"),
        rejected: () => this.log("✖ salle refusée (jeton expiré ? rechargez la page)"),
        received: (data) => this.received(data)
      }
    );
  }

  received(data) {
    if (data.sender_tab_id === this.tabId) return;
    if (data.type === "line") {
      this.showLine(data.line_id, {
        speakerName: data.speaker_name, engine: data.engine, finalText: data.final_text,
        partialText: data.partial_text, closed: data.closed, latencyMs: data.latency_ms
      });
    } else if (data.type === "engine") {
      this.engineTarget.value = data.engine;
      if (data.openai_model) this.openaiModelTarget.value = data.openai_model;
      this.engineFieldsChanged();
      this.log(`${data.speaker_name} passe sur ${ENGINES[data.engine].label}`);
      if (this.engine) this.restart();
    }
  }

  copyInvite() {
    this.inviteLinkTarget.select();
    navigator.clipboard?.writeText(this.inviteLinkTarget.value);
  }

  // ---- Engine choice, shared with the room ----

  engineChanged() {
    this.engineFieldsChanged();
    this.subscription?.perform("switch_engine", {
      sender_tab_id: this.tabId, speaker_name: this.speakerNameValue,
      engine: this.engineTarget.value, openai_model: this.openaiModelTarget.value
    });
    if (this.engine) this.restart();
  }

  engineFieldsChanged() {
    this.openaiModelFieldTarget.hidden = this.engineTarget.value !== "openai";
  }

  get engineLabel() {
    const label = ENGINES[this.engineTarget.value].label;
    return this.engineTarget.value === "openai" ? `${label} ${this.openaiModelTarget.value}` : label;
  }

  async restart() {
    if (this.restarting) return;
    this.restarting = true;
    try {
      await this.stop();
      await this.start();
    } finally {
      this.restarting = false;
    }
  }

  // ---- Start / stop ----

  async start() {
    if (this.engine || this.stream) return;

    const EngineClass = ENGINES[this.engineTarget.value];
    this.setRunning(true);
    try {
      this.setStatus("Micro…");
      this.stream = await this.openMicrophone();
      this.setStatus(`Session ${EngineClass.label}…`);
      const session = await this.createSession(EngineClass);
      if (!this.stream) return; // stopped meanwhile

      const engine = new EngineClass(this, session, {
        language: this.languageTarget.value, openaiModel: this.openaiModelTarget.value
      });
      this.engine = engine;
      this.currentEngineLabel = this.engineLabel;
      await engine.connect();
      if (this.engine !== engine) return;
      this.startAudio(EngineClass);
      this.setStatus(`En écoute · ${this.currentEngineLabel}`);
      this.log(`démarré · ${this.currentEngineLabel}`);
    } catch (error) {
      this.fail(error.message || String(error));
    }
  }

  async stop() {
    const engine = this.engine;
    if (!engine) return this.teardown("Arrêté");

    this.stopAudio();
    this.stopButtonTarget.disabled = true;
    this.setStatus("Finalisation…");
    await new Promise((resolve) => {
      this.endedResolver = resolve;
      window.setTimeout(resolve, FINISH_WAIT_MS);
      engine.finish();
    });
    this.endedResolver = null;
    if (this.engine === engine) this.teardown("Arrêté");
  }

  // Mic used in Jitsi, matched by label (device ids differ between our page and the 8x8 frame).
  async openMicrophone() {
    const constraints = { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
    let stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
    try {
      const jitsiLabel = (await this.jitsi?.getCurrentDevices())?.audioInput?.label;
      const current = stream.getAudioTracks()[0];
      if (jitsiLabel && current?.label !== jitsiLabel) {
        const match = (await navigator.mediaDevices.enumerateDevices())
          .find((device) => device.kind === "audioinput" && device.label === jitsiLabel);
        if (match) {
          stream.getTracks().forEach((track) => track.stop());
          stream = await navigator.mediaDevices.getUserMedia({ audio: { ...constraints, deviceId: { exact: match.deviceId } } });
        }
      }
    } catch (_error) {
      // keep the default microphone
    }
    this.log(`micro : ${stream.getAudioTracks()[0]?.label || "par défaut"}`);
    return stream;
  }

  async createSession(EngineClass) {
    const response = await fetch(this.sessionUrlValue.replace("ENGINE", EngineClass.id), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
      },
      body: JSON.stringify({
        language: this.languageTarget.value,
        sample_rate: EngineClass.sampleRate,
        model: this.openaiModelTarget.value
      })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
    return body;
  }

  // ---- Engine callbacks ----

  updateLine(key, { finalText, partialText, closed } = {}) {
    const lineId = `${this.tabId}:${key}`;
    const line = this.showLine(lineId, {
      speakerName: this.speakerNameValue, engine: this.currentEngineLabel, finalText, partialText,
      closed, latencyMs: closed && !this.lines.get(lineId)?.closed ? this.latencySinceVoiceEnd() : undefined
    });
    this.subscription?.perform("relay_line", {
      sender_tab_id: this.tabId, line_id: lineId, speaker_name: line.speakerName, engine: line.engine,
      final_text: line.finalText, partial_text: line.partialText, closed: line.closed, latency_ms: line.latencyMs
    });
  }

  engineError(message) {
    this.setStatus(`Erreur : ${message}`);
    this.log(`✖ ${message}`);
  }

  engineEnded(engine) {
    if (engine !== this.engine) return;
    if (this.endedResolver) return this.endedResolver();
    this.teardown(`Flux terminé par ${this.currentEngineLabel}`);
  }

  engineClosed(engine, code, reason) {
    this.log(`websocket fermé (${code}${reason ? ` · ${reason}` : ""})`);
    if (engine !== this.engine) return;
    if (this.endedResolver) return this.endedResolver();
    this.teardown(code === 1000 ? "Arrêté" : `Connexion fermée (${code})`);
  }

  // ---- Audio ----

  startAudio(EngineClass) {
    this.engineClass = EngineClass;
    this.sampleRate = EngineClass.sampleRate;
    [this.audioContext, this.source] = this.createAudioGraph(EngineClass.sampleRate);
    this.processor = this.audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
    this.processor.onaudioprocess = (event) => this.onAudio(event.inputBuffer.getChannelData(0));
    this.source.connect(this.processor);
    // A ScriptProcessor only runs while connected to the output; it writes silence there.
    this.processor.connect(this.audioContext.destination);

    this.preroll = [];
    this.speaking = false;
    this.inBurst = false;
    this.lastVoiceAt = null;
    this.previousBurstEndAt = null;
  }

  // Let the browser resample the mic (proper filtering). Firefox refuses a mic source in a context
  // at another rate, so it falls back to the device rate + resample().
  createAudioGraph(sampleRate) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    try {
      const context = new AudioContextClass({ sampleRate });
      try {
        return [context, context.createMediaStreamSource(this.stream)];
      } catch (_error) {
        context.close();
      }
    } catch (_error) {
      // fall through
    }
    const context = new AudioContextClass();
    this.log(`micro rééchantillonné en JS (${context.sampleRate} Hz → ${sampleRate} Hz)`);
    return [context, context.createMediaStreamSource(this.stream)];
  }

  onAudio(input) {
    if (!this.engine) return;
    const rms = this.muted ? 0 : rootMeanSquare(input);
    this.meterTarget.style.width = `${Math.min(100, rms * 400)}%`;
    const now = performance.now();
    const voiced = rms >= SPEECH_RMS;
    this.trackVoice(voiced, now);

    let chunk = toInt16(resample(input, this.audioContext.sampleRate, this.sampleRate));
    // Streaming engines keep receiving audio while muted (silence), so their session stays open.
    if (!this.engineClass.clientTurns) {
      if (this.muted) chunk = new Int16Array(chunk.length);
      return this.engine.sendAudio(chunk);
    }

    // Client-turn engines: send nothing while silent (keeping a short pre-roll), stream while
    // speaking, commit after `silenceMs` without voice, or discard if it held too little voice.
    if (!this.speaking) {
      if (this.muted) return;
      this.preroll.push(chunk);
      while (this.preroll.length > 1 && samplesIn(this.preroll.slice(1)) >= (this.sampleRate * PREROLL_MS) / 1000) {
        this.preroll.shift();
      }
      if (!voiced) return;

      this.speaking = true;
      this.turnStartedAt = now;
      this.turnVoicedMs = (chunk.length * 1000) / this.sampleRate; // the chunk that crossed the threshold
      this.preroll.forEach((buffered) => this.engine.sendAudio(buffered));
      this.preroll = [];
      return;
    }

    this.engine.sendAudio(chunk);
    if (voiced) this.turnVoicedMs += (chunk.length * 1000) / this.sampleRate;
    if (now - this.lastVoiceAt >= Number(this.silenceMsTarget.value || 700)) {
      if (this.turnVoicedMs >= MIN_VOICED_MS) {
        this.engine.commit();
      } else {
        this.engine.discard();
        this.log(`bruit ignoré (${Math.round(this.turnVoicedMs)} ms de voix)`);
      }
      this.speaking = false;
    } else if (now - this.turnStartedAt >= MAX_TURN_MS) {
      this.engine.commit();
      this.turnStartedAt = now;
      this.turnVoicedMs = 0;
    }
  }

  // Bursts of speech, for the latency shown on each line: from the moment the speaker stopped
  // talking to the moment the final text arrived.
  trackVoice(voiced, now) {
    if (voiced) {
      if (!this.inBurst) this.previousBurstEndAt = this.lastVoiceAt;
      this.inBurst = true;
      this.lastVoiceAt = now;
    } else if (this.inBurst && now - this.lastVoiceAt > VOICE_HANGOVER_MS) {
      this.inBurst = false;
    }
  }

  latencySinceVoiceEnd() {
    // Already talking again: the sentence that just closed ended with the previous burst.
    const endedAt = this.inBurst ? this.previousBurstEndAt : this.lastVoiceAt;
    return endedAt ? Math.round(performance.now() - endedAt) : null;
  }

  stopAudio() {
    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
    }
    this.source?.disconnect();
    this.audioContext?.close();
    this.processor = this.source = this.audioContext = null;
    if (this.hasMeterTarget) this.meterTarget.style.width = "0";
  }

  teardown(status = "Prêt") {
    this.stopAudio();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    const engine = this.engine;
    this.engine = null;
    engine?.close();

    if (!this.hasStartButtonTarget) return; // page already gone
    this.setRunning(false);
    this.setStatus(status);
  }

  fail(message) {
    this.log(`✖ ${message}`);
    this.teardown(`Erreur : ${message}`);
  }

  // ---- Display ----

  // One paragraph per sentence, keyed by "<tab>:<engine>-<n>", for our lines and relayed ones.
  showLine(lineId, attributes) {
    let line = this.lines.get(lineId);
    if (!line) {
      const element = document.createElement("p");
      element.className = "transcription-lab__line";
      element.innerHTML = '<strong></strong> <small class="transcription-lab__engine"></small> ' +
        '<span class="transcription-lab__final"></span><span class="transcription-lab__partial"></span>' +
        '<small class="transcription-lab__latency"></small>';
      this.transcriptTarget.append(element);
      line = { element, finalText: "", partialText: "", closed: false, latencyMs: null };
      this.lines.set(lineId, line);
    }
    Object.entries(attributes).forEach(([name, value]) => {
      if (value !== undefined && value !== null) line[name] = value;
    });

    const [speaker, engine, final, partial, latency] = line.element.children;
    speaker.textContent = line.speakerName;
    engine.textContent = line.engine;
    final.textContent = line.finalText;
    partial.textContent = line.finalText && line.partialText ? ` ${line.partialText}` : line.partialText;
    latency.textContent = line.closed && line.latencyMs ? `  +${line.latencyMs} ms` : "";
    line.element.hidden = !line.finalText && !line.partialText;
    if (line.closed && !line.finalText) {
      line.element.remove(); // silence or noise: nothing was said
      this.lines.delete(lineId);
    }
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight;
    return line;
  }

  clear() {
    this.transcriptTarget.textContent = "";
    this.logTarget.textContent = "";
    this.lines.clear();
  }

  setRunning(running) {
    this.startButtonTarget.disabled = running;
    this.stopButtonTarget.disabled = !running;
    this.languageTarget.disabled = running;
  }

  setStatus(text) {
    this.statusTarget.textContent = text;
  }

  log(message) {
    const time = new Date().toLocaleTimeString("fr-FR");
    this.logTarget.textContent += `${time}  ${message}\n`;
    this.logTarget.scrollTop = this.logTarget.scrollHeight;
  }
}

function rootMeanSquare(samples) {
  let sum = 0;
  for (let index = 0; index < samples.length; index += 1) sum += samples[index] * samples[index];
  return Math.sqrt(sum / samples.length);
}

function samplesIn(chunks) {
  return chunks.reduce((total, chunk) => total + chunk.length, 0);
}

// Fallback only (see createAudioGraph): plain linear interpolation, no anti-aliasing filter.
function resample(input, fromRate, toRate) {
  if (fromRate === toRate) return input;

  const ratio = fromRate / toRate;
  const output = new Float32Array(Math.floor(input.length / ratio));
  for (let index = 0; index < output.length; index += 1) {
    const position = index * ratio;
    const left = Math.floor(position);
    const right = Math.min(left + 1, input.length - 1);
    output[index] = input[left] + (input[right] - input[left]) * (position - left);
  }
  return output;
}

function toInt16(samples) {
  const output = new Int16Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}
